import {
  Bell,
  BookOpen,
  Download,
  Dna,
  EyeOff,
  Flame,
  GitBranch,
  Lock,
  QrCode,
  Shield,
} from 'lucide-react';

/** Footer navigation and public site link groups — no fake external URLs. */

export const PUBLIC_DEMO_ROUTE = '/how-it-works';

export const FOOTER_PRODUCT_LINKS = [
  { label: 'AI Story Studio', to: '/story-studio' },
  { label: 'Family Handover Vault', to: '/vault' },
  { label: 'Memorial Bridge', to: '/memorials' },
  { label: 'How it works', to: PUBLIC_DEMO_ROUTE },
  { label: 'Pricing', to: '/pricing' },
];

export const FOOTER_FOR_FAMILIES_LINKS = [
  { label: 'Start your archive', to: '/register' },
  { label: 'Add voice memories', to: `${PUBLIC_DEMO_ROUTE}#voice-memories` },
  { label: 'Preserve documents', to: `${PUBLIC_DEMO_ROUTE}#documents-possessions` },
  { label: 'Priceless possessions', to: `${PUBLIC_DEMO_ROUTE}#documents-possessions` },
  { label: 'Legacy Book', to: '/pricing#legacy-book' },
];

export const FOOTER_CHAPTERS_LINKS = [
  { label: 'First Chapter', to: '/#getting-started' },
  { label: 'Life Story', to: PUBLIC_DEMO_ROUTE },
  { label: 'Memorial Bridge', to: '/memorials' },
  { label: 'Legacy Book', to: '/pricing#legacy-book' },
];

export const FOOTER_TRUST_BADGES = [
  { label: 'Private by default', icon: 'shield' },
  { label: 'Export anytime', icon: 'download' },
  { label: 'Memorials only when you publish', icon: 'flame' },
];

export const FOOTER_TRUST_LINKS = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Data deletion / privacy request', to: '/privacy#data' },
  { label: 'Memorial guidance', to: '/faq#memorial' },
  { label: 'DNA privacy / Beta note', to: '/faq#dna' },
];

export const FOOTER_SUPPORT_LINKS = [
  { label: 'Help & Guides', to: '/help' },
  { label: 'Contact', to: '/contact' },
  { label: 'Support', to: '/faq' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Partners', to: '/partners' },
];

/** Compact footer links for public memorial and tribute pages. */
export const FOOTER_MEMORIAL_LINKS = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Contact', to: '/contact' },
  { label: 'Memorial guidance', to: '/faq#memorial' },
  { label: 'Data deletion', to: '/privacy#data' },
];

/** Minimal footer links for auth pages. */
export const FOOTER_MINIMAL_LINKS = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Contact', to: '/contact' },
  { label: 'Help', to: '/help' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Support', to: '/faq' },
];

/** Real trust cues — no fake testimonials or user counts. */
export const TRUST_PROOF_ITEMS = [
  {
    icon: Shield,
    title: 'Private by default',
    description: 'Your archive stays closed until you invite family or choose to publish.',
  },
  {
    icon: Lock,
    title: 'You control sharing',
    description: 'Invite-only access, memorial visibility, and export — all in your hands.',
  },
  {
    icon: Download,
    title: 'Export your archive anytime',
    description: 'Download a keepsake Legacy Book PDF or a full backup when you are ready.',
  },
  {
    icon: Flame,
    title: 'Memorials are optional and moderated',
    description: 'Create tribute pages only when the time is right. You approve what goes live.',
  },
  {
    icon: Dna,
    title: 'DNA upload is beta and private',
    description: 'Optional genetic files stay in your archive — never sold or shared publicly.',
  },
  {
    icon: GitBranch,
    title: 'Family connections can be verified',
    description: 'Link relatives with invites and optional surname discovery you opt into.',
  },
  {
    icon: EyeOff,
    title: 'Public pages only when you publish',
    description: 'Memorial and tribute pages exist only after you deliberately make them live.',
  },
];

export const KEY_PRODUCT_FEATURES = [
  {
    icon: BookOpen,
    title: 'Storybook Mode',
    copy: 'Sixteen guided chapters — one prompt at a time, at your own pace.',
    anchor: '/how-it-works#story-prompts',
  },
  {
    icon: Download,
    title: 'Legacy Book PDF',
    copy: 'Turn preserved stories, voices and documents into a keepsake book.',
    anchor: '/pricing#legacy-book',
  },
  {
    icon: Flame,
    title: 'Memorial Bridge',
    copy: 'A respectful tribute page from preserved memories — private until you publish.',
    anchor: '/faq#memorial',
  },
  {
    icon: QrCode,
    title: 'QR Keepsakes',
    copy: 'Printable codes that link to a memorial — update the page without reprinting.',
    anchor: '/faq#memorial',
  },
  {
    icon: Bell,
    title: 'Reminders',
    copy: 'Birthdays, remembrance days and anniversaries — kept close, privately.',
    anchor: '/register',
  },
  {
    icon: Shield,
    title: 'Private archive',
    copy: 'One calm home for voices, words, photos and family context.',
    anchor: '/register',
  },
];

/** Homepage pricing preview — aligned with /pricing (Stage 4B.1). */
export const PRICING_PREVIEW_PLANS = [
  {
    name: 'Start Your Legacy',
    price: 'Free',
    note: 'Preview your Legacy Book',
    highlight: false,
  },
  {
    name: 'Legacy Book',
    price: '£49 founding',
    note: 'Finished keepsake PDF',
    highlight: true,
  },
  {
    name: 'Family Archive',
    price: 'From £7.99/mo',
    note: 'Subscription coming soon',
    highlight: false,
  },
];

/** Full life circle — homepage messaging */
export const LIFE_CIRCLE_CHAPTERS = [
  {
    key: 'firstChapter',
    title: 'First Chapter',
    copy: 'Welcome a new life with photos, family messages, voice notes and first milestones.',
    note: 'Birth, adoption, chosen family welcome, or naming day — private from the start.',
  },
  {
    key: 'lifeStory',
    title: 'Life Story',
    copy: 'Capture stories, voice memories, documents and priceless possessions as life unfolds.',
    note: 'People → Memories → Legacy Book when you are ready.',
  },
  {
    key: 'memorial',
    title: 'Memorial Bridge',
    copy: 'Create a respectful tribute page when the time is right, with tributes, QR keepsakes and family memories.',
    note: 'Optional lifetime memorial page — not a monthly subscription.',
  },
];

/** Future export concept — connects to Legacy Book PDF later */
export const FIRST_CHAPTER_EXPORT = {
  title: 'First Chapter PDF',
  tagline: 'A keepsake for the beginning of their story.',
  description:
    'Early photos, welcome messages and first milestones — gathered into a gentle keepsake, alongside your full Legacy Book when export expands.',
};
